import { useContext, useMemo, useState } from "react";
import { Modal, Progress, Tooltip, Input, Empty, Space, Tag } from "antd";
import { HeartOutlined, SearchOutlined } from "@ant-design/icons";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import { formatCnpj } from "../../../../utils/Masks";
import { poeValorComPontosEmMil } from "../../../../utils/Conversores";
import { ISocialInstitution } from "../../../../interfaces";

interface IProps {
    open: boolean;
    onClose: () => void;
    institutions: ISocialInstitution[];
    totalLinkedUsers: number;
    totalUsers: number;
    onSelectInstitution?: (institution: ISocialInstitution) => void;
}

export default function ModalSupportDistribution({
    open,
    onClose,
    institutions,
    totalLinkedUsers,
    totalUsers,
    onSelectInstitution,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const [search, setSearch] = useState<string>('');

    const sortedInstitutions = useMemo(() => {
        return [...institutions].sort((a, b) => (b.total_users || 0) - (a.total_users || 0));
    }, [institutions]);

    const filteredInstitutions = useMemo(() => {
        if (!search.trim()) return sortedInstitutions;
        const s = search.toLowerCase();
        return sortedInstitutions.filter(
            (inst) =>
                inst.name?.toLowerCase().includes(s) ||
                inst.cnpj?.replace(/\D/g, '').includes(s.replace(/\D/g, ''))
        );
    }, [sortedInstitutions, search]);

    return (
        <Modal
            width={780}
            open={open}
            onCancel={onClose}
            footer={null}
            centered
            destroyOnClose
            title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 24 }}>
                    <Space size={10} align="center">
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                background: isDark ? 'rgba(235, 47, 150, 0.15)' : '#fff0f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <HeartOutlined style={{ color: '#eb2f96', fontSize: 18 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
                                Distribuição de Apoio por Entidade
                            </div>
                            <div style={{ fontSize: 12, color: isDark ? '#8c8c8c' : '#64748b', marginTop: 2 }}>
                                Engajamento comunitário e suporte dos cidadãos às instituições
                            </div>
                        </div>
                    </Space>
                    <Tag color="pink" style={{ borderRadius: 6, fontWeight: 600, fontSize: 12, padding: '2px 8px' }}>
                        {poeValorComPontosEmMil(totalLinkedUsers)} apoiadores
                    </Tag>
                </div>
            }
            bodyStyle={{ padding: '16px 0 0 0' }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Input
                    placeholder="Buscar entidade por nome ou CNPJ..."
                    prefix={<SearchOutlined style={{ color: isDark ? '#64748b' : '#94a3b8' }} />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    allowClear
                    style={{
                        borderRadius: 8,
                        height: 38,
                    }}
                />
                {filteredInstitutions.length === 0 ? (
                    <div style={{ padding: '36px 0' }}>
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span style={{ color: isDark ? '#8c8c8c' : '#64748b' }}>
                                    Nenhuma instituição social encontrada
                                </span>
                            }
                        />
                    </div>
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                            maxHeight: 460,
                            overflowY: 'auto',
                            paddingRight: 4,
                        }}
                    >
                        {filteredInstitutions.map((inst, index) => {
                            const count = inst.total_users || 0;
                            const percent =
                                totalLinkedUsers > 0
                                    ? Math.min(100, Math.round((count / totalLinkedUsers) * 100))
                                    : 0;

                            return (
                                <div
                                    key={inst.id || index}
                                    style={{
                                        padding: '12px 16px',
                                        borderRadius: 10,
                                        background: isDark ? '#141414' : '#fafafa',
                                        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                        borderLeft: '4px solid #eb2f96',
                                        transition: 'all 0.18s ease',
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Tooltip title={inst.name}>
                                                <div
                                                    style={{
                                                        fontWeight: 600,
                                                        fontSize: 15,
                                                        color: isDark ? '#f0f0f0' : '#1e293b',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {inst.name}
                                                </div>
                                            </Tooltip>
                                            <div style={{ fontSize: 12.5, color: isDark ? '#8c8c8c' : '#64748b', marginTop: 2 }}>
                                                {formatCnpj(inst.cnpj)}
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontWeight: 700, fontSize: 16, color: '#eb2f96' }}>
                                                    {poeValorComPontosEmMil(count)} {count === 1 ? 'apoiador' : 'apoiadores'}
                                                </div>
                                                <div style={{ fontSize: 12, color: isDark ? '#8c8c8c' : '#64748b' }}>
                                                    {percent}% do total
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: 8 }}>
                                        <Progress
                                            percent={percent}
                                            size="small"
                                            strokeColor="#eb2f96"
                                            trailColor={isDark ? '#262626' : '#f0f0f0'}
                                            showInfo={false}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Modal>
    );
}
